import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Search,
  Plus,
  Users,
  User,
  Edit,
  Ban,
  CheckCircle,
  Mail,
  Loader2,
  UserCog,
  MoreHorizontal,
  ChevronUp,
  ChevronDown,
  X,
  Clock,
  SlidersHorizontal,
} from 'lucide-react';
import UserFormDialog from '@/components/clinic/UserFormDialog';
import { toast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { clinicUserService, ClinicUser } from '@/services/clinicUserService';
import { roleService, Role } from '@/services/roleService';
import { useAuth } from '@/contexts/AuthContext';
import DeactivateUserDialog from '@/components/dialog/DeactivateUserDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetClose,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import SmartPagination from '@/components/ui/smart-pagination';
import { useClinic } from '@/contexts/ClinicContext';

// Filter options
const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'invited', label: 'Invited' },
  { value: 'inactive', label: 'Blocked' },
  { value: 'archived', label: 'Archived' },
];

const UserManagement = () => {
  const { user } = useAuth();
  const { clinic } = useClinic();
  const clinicId = clinic?.id || '';

  const [users, setUsers] = useState<ClinicUser[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<ClinicUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [openStatusToggleDialog, setOpenStatusToggleDialog] = useState(false);
  const [userToToggleStatus, setUserToToggleStatus] = useState<ClinicUser | null>(null);

  // Filter drawer state
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  // Temp filter states (for drawer)
  const [tempStatusFilter, setTempStatusFilter] = useState<string>('all');
  const [tempRoleFilter, setTempRoleFilter] = useState<string>('all');
  const [tempSortBy, setTempSortBy] = useState<string>('created_at');
  const [tempSortOrder, setTempSortOrder] = useState<'asc' | 'desc'>('desc');

  // Applied filter states
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 1,
  });

  // Debounced search - only updates the debounced search term
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  // Fetch roles on mount
  useEffect(() => {
    if (clinicId) {
      fetchRoles();
    }
  }, [clinicId]);

  // Single effect to handle all fetching
  useEffect(() => {
    if (clinicId) {
      fetchUsers();
    }
  }, [clinicId, statusFilter, roleFilter, sortField, sortOrder, pagination.page, debouncedSearchTerm]);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const response = await clinicUserService.getClinicUsers(clinicId, {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        status: statusFilter === 'all' ? '' : statusFilter,
        role: roleFilter === 'all' ? '' : roleFilter,
        sortBy: sortField,
        sortOrder: sortOrder.toUpperCase() as 'ASC' | 'DESC',
      });
      setUsers(response.data.users);
      if (response.data.pagination) {
        setPagination(prev => ({
          ...prev,
          totalItems: response.data.pagination.totalItems || response.data.users.length,
          totalPages: response.data.pagination.totalPages || 1,
        }));
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error?.message || 'Failed to fetch users',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await roleService.getClinicRoles(clinicId, { page: 1, limit: 100 });
      setRoles(response.data.roles);
    } catch (error: any) {
      console.error('Failed to fetch roles:', error);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return <ChevronUp className="h-3 w-3 text-slate-300" />;
    return sortOrder === 'asc' ? (
      <ChevronUp className="h-3 w-3 text-slate-700" />
    ) : (
      <ChevronDown className="h-3 w-3 text-slate-700" />
    );
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setIsDialogOpen(true);
  };

  const handleEditUser = (user: ClinicUser) => {
    setEditingUser(user);
    setIsDialogOpen(true);
  };

  const handleInviteUser = async (userId: string) => {
    try {
      setActionLoading(userId);
      await clinicUserService.inviteUser(clinicId, userId);

      toast({
        title: 'Invitation Sent',
        description: 'User invitation has been sent successfully.',
        variant: 'success',
      });

      await fetchUsers();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error?.message || 'Failed to send invitation',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveUser = async (userData: any) => {
    try {
      setIsSaving(true);
      if (editingUser) {
        await clinicUserService.updateClinicUser(clinicId, editingUser.id, {
          first_name: userData.first_name,
          last_name: userData.last_name,
          phone_number: userData.phone_number,
          role_id: userData.role_id,
        });

        toast({
          title: 'User Updated',
          description: `${userData.first_name} has been updated successfully.`,
          variant: 'success',
        });
      } else {
        await clinicUserService.createClinicUser(clinicId, {
          first_name: userData.first_name,
          last_name: userData.last_name,
          email: userData.email,
          role_id: userData.role_id,
          phone_number: userData.phone_number,
        });

        toast({
          title: 'User Created',
          description: `${userData.first_name} has been created and invited successfully.`,
          variant: 'success',
        });
      }

      setIsDialogOpen(false);
      await fetchUsers();
    } catch (error: any) {
      console.error('Failed to save user:', error);
      toast({
        title: 'Error',
        description:
          error.response?.data?.error?.message ||
          error.message ||
          `Failed to ${editingUser ? 'update' : 'create'} user`,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-emerald-600 hover:bg-emerald-600 text-white border-0';
      case 'invited':
        return 'bg-amber-500 hover:bg-amber-500 text-white border-0';
      case 'inactive':
        return 'bg-red-500 hover:bg-red-500 text-white border-0';
      case 'archived':
        return 'bg-slate-500 hover:bg-slate-500 text-white border-0';
      default:
        return 'bg-slate-100 hover:bg-slate-100 text-slate-800 border-0';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-3 w-3" />;
      case 'invited':
        return <Clock className="h-3 w-3" />;
      case 'inactive':
        return <Ban className="h-3 w-3" />;
      case 'archived':
        return <Ban className="h-3 w-3" />;
      default:
        return null;
    }
  };

  const getUserTypeLabel = (type: string) => {
    if (type === 'clinic_admin') return 'Admin';
    if (type === 'clinic_staff') return 'Clinic Staff';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  // Rotate colors for user icons
  const colors = [
    { bg: 'bg-blue-100', text: 'text-blue-600' },
    { bg: 'bg-purple-100', text: 'text-purple-600' },
    { bg: 'bg-pink-100', text: 'text-pink-600' },
    { bg: 'bg-orange-100', text: 'text-orange-600' },
    { bg: 'bg-amber-100', text: 'text-amber-600' },
    { bg: 'bg-cyan-100', text: 'text-cyan-600' },
    { bg: 'bg-indigo-100', text: 'text-indigo-600' },
    { bg: 'bg-rose-100', text: 'text-rose-600' },
    { bg: 'bg-violet-100', text: 'text-violet-600' },
    { bg: 'bg-teal-100', text: 'text-teal-600' },
  ];

  // Check for active filters
  const hasActiveFilters =
    statusFilter !== 'all' ||
    roleFilter !== 'all' ||
    sortField !== 'created_at' ||
    sortOrder !== 'desc';

  const getActiveFilterCount = () => {
    let count = 0;
    if (statusFilter !== 'all') count++;
    if (roleFilter !== 'all') count++;
    if (sortField !== 'created_at' || sortOrder !== 'desc') count++;
    return count;
  };

  const openFilterDrawer = () => {
    // Set temp values to current applied values
    setTempStatusFilter(statusFilter);
    setTempRoleFilter(roleFilter);
    setTempSortBy(sortField);
    setTempSortOrder(sortOrder);
    setIsFilterDrawerOpen(true);
  };

  const applyFilters = () => {
    setStatusFilter(tempStatusFilter);
    setRoleFilter(tempRoleFilter);
    setSortField(tempSortBy);
    setSortOrder(tempSortOrder);
    setPagination(prev => ({ ...prev, page: 1 }));
    setIsFilterDrawerOpen(false);
  };

  const resetFilters = () => {
    setTempStatusFilter('all');
    setTempRoleFilter('all');
    setTempSortBy('created_at');
    setTempSortOrder('desc');
  };

  const clearFilter = (type: string) => {
    if (type === 'status') {
      setStatusFilter('all');
    } else if (type === 'role') {
      setRoleFilter('all');
    } else if (type === 'sort') {
      setSortField('created_at');
      setSortOrder('desc');
    }
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearAllFilters = () => {
    setStatusFilter('all');
    setRoleFilter('all');
    setSortField('created_at');
    setSortOrder('desc');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-montserrat text-slate-900 tracking-tight text-xl sm:text-2xl font-bold">
            User Management
          </h1>
          <p className="text-slate-500 text-sm mt-1">Manage your clinic's staff and users</p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <Button
            onClick={handleCreateUser}
            className="h-10 px-4 flex-1 sm:flex-none rounded-lg bg-brand-teal hover:bg-brand-teal/90 text-white gap-2"
          >
            <Plus className="h-4 w-4" />
            Add User
          </Button>
        </div>
      </div>

      {/* Filters Row */}
      <div className="flex flex-row items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input
            placeholder="Search by name or email..."
            className="pl-9 bg-white border-slate-200 rounded-lg"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value.trimStart())}
          />
        </div>

        {/* Filter Button */}
        <Button
          variant="outline"
          size="sm"
          className="h-9 border-slate-200 text-slate-700 rounded-lg ml-auto hover:bg-slate-50 hover:text-slate-700 gap-2"
          onClick={openFilterDrawer}
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filter
          {getActiveFilterCount() > 0 && (
            <Badge className="ml-1 bg-red-500 text-white border-0 text-xs px-1.5 py-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full">
              {getActiveFilterCount()}
            </Badge>
          )}
        </Button>
      </div>

      {/* Active Filters Pills */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-slate-500">Filters:</span>
          {statusFilter !== 'all' && (
            <Badge variant="secondary" className="text-xs px-2 py-1 bg-white text-foreground hover:bg-white border border-border">
              Status: {STATUS_OPTIONS.find(o => o.value === statusFilter)?.label}
              <button onClick={() => clearFilter('status')} className="ml-1.5 hover:text-slate-900">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {roleFilter !== 'all' && (
            <Badge variant="secondary" className="text-xs px-2 py-1 bg-white text-foreground hover:bg-white border border-border">
              Role: {roles.find(r => r.id === roleFilter)?.name}
              <button onClick={() => clearFilter('role')} className="ml-1.5 hover:text-slate-900">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {sortField !== 'created_at' && (
            <Badge variant="secondary" className="text-xs px-2 py-1 bg-white text-foreground hover:bg-white border border-border">
              Sort: {sortField === 'first_name' ? 'Name' : sortField === 'status' ? 'Status' : sortField}
              <button onClick={() => clearFilter('sort')} className="ml-1.5 hover:text-slate-900">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {sortOrder !== 'desc' && sortField === 'created_at' && (
            <Badge variant="secondary" className="text-xs px-2 py-1 bg-white text-foreground hover:bg-white border border-border">
              Order: Ascending
              <button onClick={() => clearFilter('sort')} className="ml-1.5 hover:text-slate-900">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          <button
            onClick={clearAllFilters}
            className="text-xs text-brand-teal hover:underline"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px] text-sm">
            <thead style={{ backgroundColor: 'rgb(245, 247, 247)' }}>
              <tr className="border-b border-slate-200">
                <th
                  className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider w-12"
                  style={{ fontSize: '0.7rem' }}
                >
                  #
                </th>
                <th
                  className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:text-slate-900"
                  style={{ fontSize: '0.7rem' }}
                  onClick={() => handleSort('first_name')}
                >
                  <div className="flex items-center gap-1">
                    Name
                    <SortIcon field="first_name" />
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider"
                  style={{ fontSize: '0.7rem' }}
                >
                  Email
                </th>
                <th
                  className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider"
                  style={{ fontSize: '0.7rem' }}
                >
                  Type
                </th>
                <th
                  className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider"
                  style={{ fontSize: '0.7rem' }}
                >
                  Role
                </th>
                <th
                  className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:text-slate-900"
                  style={{ fontSize: '0.7rem' }}
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Status
                    <SortIcon field="status" />
                  </div>
                </th>
                <th
                  className="w-12 px-2 py-3 sticky right-0"
                  style={{
                    background:
                      'linear-gradient(to right, transparent 0%, rgb(248 250 252) 20%, rgb(248 250 252) 100%)',
                  }}
                ></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={index} className="border-b border-slate-100">
                    <td className="px-4 py-3">
                      <Skeleton className="h-4 w-10" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-8 w-8 rounded-lg" />
                        <Skeleton className="h-4 w-24" />
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-4 w-32" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-5 w-20 rounded-full" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-5 w-16 rounded-full" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-5 w-16 rounded-full" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-8 w-8 rounded" />
                    </td>
                  </tr>
                ))
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="h-48 text-center">
                    <div className="flex flex-col items-center justify-center text-slate-500">
                      <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                        <Users className="h-7 w-7 text-slate-400" />
                      </div>
                      <p className="font-medium text-slate-600">
                        {searchTerm ? 'No users found' : 'No users yet'}
                      </p>
                      <p className="text-sm text-slate-400 mt-1">
                        {searchTerm ? 'Try adjusting your search' : 'Add your first user to get started'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                users.map((userItem, index) => {
                  const color = colors[index % colors.length];
                  return (
                    <tr
                      key={userItem.id}
                      className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                    >
                      <td
                        className="px-4 py-3 text-slate-500 whitespace-nowrap"
                        style={{ fontSize: '0.8rem' }}
                      >
                        #{String((pagination.page - 1) * pagination.limit + index + 1).padStart(3, '0')}
                      </td>
                      <td className="px-4 py-3 max-w-[200px] whitespace-nowrap" style={{ fontSize: '0.8rem' }}>
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-8 h-8 ${color.bg} rounded-lg flex items-center justify-center flex-shrink-0`}
                          >
                            <User className={`h-4 w-4 ${color.text}`} />
                          </div>
                          <div className="min-w-0">
                            <span className="font-medium text-slate-900 truncate block max-w-[150px] capitalize">
                              {userItem.first_name} {userItem.last_name}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 max-w-[180px]" style={{ fontSize: '0.8rem' }}>
                        <span className="block truncate" title={userItem.email || '--'}>
                          {userItem.email || '--'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap" style={{ fontSize: '0.8rem' }}>
                        <Badge variant="outline" className="text-xs bg-slate-50 border-slate-200">
                          <UserCog className="h-3 w-3 mr-1" />
                          {getUserTypeLabel(userItem.user_type)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap" style={{ fontSize: '0.8rem' }}>
                        {userItem.role ? (
                          <Badge
                            variant="outline"
                            className="text-xs bg-blue-50 text-blue-700 border-blue-200"
                          >
                            {userItem.role.name}
                          </Badge>
                        ) : (
                          <span className="text-slate-400 text-xs">No Role</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Badge
                          className={`${getStatusColor(userItem.status)} text-xs inline-flex items-center gap-1 w-fit`}
                        >
                          {getStatusIcon(userItem.status)}
                          <span className="capitalize">{userItem.status}</span>
                        </Badge>
                      </td>
                      <td
                        className="w-12 px-2 py-3 sticky right-0"
                        style={{
                          background:
                            'linear-gradient(to right, transparent 0%, white 20%, white 100%)',
                        }}
                      >
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-slate-100">
                              <MoreHorizontal className="h-4 w-4 text-slate-500" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-40">
                            <DropdownMenuItem
                              onClick={() => handleEditUser(userItem)}
                              className="cursor-pointer"
                              style={{ fontSize: '13px' }}
                              disabled={!!actionLoading}
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => {
                                setUserToToggleStatus(userItem);
                                setOpenStatusToggleDialog(true);
                              }}
                              className={`cursor-pointer ${userItem.status === 'active' ? 'text-red-600' : 'text-emerald-600'}`}
                              style={{ fontSize: '13px' }}
                            >
                              {userItem.status === 'active' ? (
                                <>
                                  <Ban className="h-4 w-4 mr-2" />
                                  Deactivate
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Activate
                                </>
                              )}
                            </DropdownMenuItem>
                            {(userItem.status === 'invited' || userItem.status === 'inactive') && (
                              <DropdownMenuItem
                                onClick={() => handleInviteUser(userItem.id)}
                                className="cursor-pointer text-blue-600"
                                style={{ fontSize: '13px' }}
                                disabled={!!actionLoading}
                              >
                                {actionLoading === userItem.id ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                  <Mail className="h-4 w-4 mr-2" />
                                )}
                                {userItem.status === 'invited' ? 'Resend Invite' : 'Send Invite'}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="border-t border-slate-200 px-2 sm:px-4 py-3 flex flex-col sm:flex-row items-center sm:justify-between gap-3">
            <div className="w-full sm:w-auto overflow-x-auto">
              <SmartPagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
                className="mx-0 justify-center sm:justify-start"
                maxVisiblePages={3}
              />
            </div>
            <p className="text-xs text-slate-500 whitespace-nowrap">
              Total: <span className="font-medium text-slate-700">{pagination.totalItems}</span> Users
            </p>
          </div>
        )}
      </div>

      {/* User Form Dialog */}
      <UserFormDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        user={editingUser}
        onSave={handleSaveUser}
        isLoading={isSaving}
      />

      {/* User Status Toggle Confirmation Dialog */}
      <DeactivateUserDialog
        open={openStatusToggleDialog}
        onOpenChange={setOpenStatusToggleDialog}
        user={userToToggleStatus || null}
        onSuccess={() => {
          setOpenStatusToggleDialog(false);
          fetchUsers();
        }}
      />

      {/* Filter Drawer */}
      <Sheet open={isFilterDrawerOpen} onOpenChange={setIsFilterDrawerOpen}>
        <SheetContent
          className="w-full max-w-[400px] rounded-l-2xl p-0 flex flex-col gap-0 [&>button]:hidden"
          style={{ minWidth: 'auto' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <SheetTitle className="font-semibold text-slate-900" style={{ fontSize: '1.05rem' }}>
              Filter & Sort
            </SheetTitle>
            <SheetClose asChild>
              <button className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors">
                <X className="h-5 w-5 text-slate-600" />
              </button>
            </SheetClose>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
            {/* Status Filter */}
            <div className="space-y-1">
              <Label className="font-medium" style={{ fontSize: '0.75rem', color: '#0f172a' }}>
                Status
              </Label>
              <Select value={tempStatusFilter} onValueChange={setTempStatusFilter}>
                <SelectTrigger className="w-full h-10 border-slate-200 focus:ring-0 focus:border-black focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none transition-colors duration-200">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {STATUS_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Role Filter */}
            <div className="space-y-1">
              <Label className="font-medium" style={{ fontSize: '0.75rem', color: '#0f172a' }}>
                Role
              </Label>
              <Select value={tempRoleFilter} onValueChange={setTempRoleFilter}>
                <SelectTrigger className="w-full h-10 border-slate-200 focus:ring-0 focus:border-black focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none transition-colors duration-200">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {roles.map(role => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Sort By */}
            <div className="space-y-1">
              <Label className="font-medium" style={{ fontSize: '0.75rem', color: '#0f172a' }}>
                Sort By
              </Label>
              <Select value={tempSortBy} onValueChange={setTempSortBy}>
                <SelectTrigger className="w-full h-10 border-slate-200 focus:ring-0 focus:border-black focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none transition-colors duration-200">
                  <SelectValue placeholder="Select field" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">Date Created</SelectItem>
                  <SelectItem value="first_name">Name</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Sort Order */}
            <div className="space-y-1">
              <Label className="font-medium" style={{ fontSize: '0.75rem', color: '#0f172a' }}>
                Sort Order
              </Label>
              <Select value={tempSortOrder} onValueChange={value => setTempSortOrder(value as 'asc' | 'desc')}>
                <SelectTrigger className="w-full h-10 border-slate-200 focus:ring-0 focus:border-black focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none transition-colors duration-200">
                  <SelectValue placeholder="Select order" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asc">Ascending</SelectItem>
                  <SelectItem value="desc">Descending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
            <Button
              variant="ghost"
              className="text-slate-700 hover:text-slate-900 hover:bg-transparent font-medium"
              onClick={resetFilters}
            >
              Reset all
            </Button>
            <Button
              className="bg-brand-teal hover:bg-brand-teal/90 rounded-lg h-11 px-8"
              onClick={applyFilters}
            >
              Apply Filter
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default UserManagement;
